import React, { useRef, useState } from 'react';
import { getLocale, useIntl } from 'umi';
import { notification } from 'tea-component';
import { IComponentRefreshProps } from '@/pages/dashboard/components/PageContainer/type';
import { RefreshContext } from '@/pages/dashboard/components/PageContainer/context';

const checkArraysEqual = (a: string[], b: string[]) => {
  if (a?.length !== b?.length || !a?.length || !b?.length) {
    return false;
  }
  const sortedA = a?.slice()?.sort();
  const sortedB = b?.slice()?.sort();
  return sortedA?.every((value: string, index: number) => value === sortedB[index]);
};

// 配置化点击刷新组件
const Refresh = (props: IComponentRefreshProps) => {
  const { children, config } = props;
  const {
    refreshSuccessEnMsg = '',
    refreshSuccessCnMsg = '',
    refreshFailEnMsg = '',
    refreshFailCnMsg = '',
    onComplete,
    onSuccess,
    onFail
  } = config || {};
  const { formatMessage } = useIntl();
  const isEn = getLocale() === 'en-US';
  // forceRefresh: 刷新状态
  const [forceRefresh, setForceRefresh] = useState<boolean>(false);
  // apiListRef: 点击刷新控制的组件列表
  const apiListRef = useRef<string[]>([]);
  // apiReqSuccessedListRef: 刷新成功的组件列表
  const apiReqSuccessedListRef = useRef<string[]>([]);
  // apiReqFailedListRef: 刷新失败的组件列表
  const apiReqFailedListRef = useRef<string[]>([]);

  const apiReqCallback = (refreshKey: string, successList: string[], failtList: string[]) => {
    // 兜底：不包含在apilist之内则直接返回，不做处理
    if (!apiListRef?.current?.includes(refreshKey)) return;
    // 分别赋值刷新成功和失败的list
    apiReqSuccessedListRef.current = successList;
    apiReqFailedListRef.current = failtList;
    const complateList = [...successList, ...failtList];
    const isEqual = checkArraysEqual(complateList, apiListRef?.current);
    // 请求完成后做对应的请求成功和请求失败的处理
    if (isEqual) {
      // 存在请求失败结果
      if (failtList?.length > 0) {
        onRefreshFail();
      } else {
        // 请求成功
        onRefreshSuccess();
      }
      // 请求完成回调
      onRefreshComplete();
    }
  };

  // 收集service中刷新的refreshKey，用于请求成功请求失败的判断
  const apiCollect = (refreshKey: string) => {
    // 如果已存在则不收集
    if (apiListRef?.current?.includes(refreshKey)) return;
    apiListRef.current = [...apiListRef?.current, refreshKey];
  };

  // 单个组件请求成功处理
  const apiSuccess = (refreshKey: string) => {
    apiReqCallback(refreshKey, [...apiReqSuccessedListRef?.current, refreshKey], apiReqFailedListRef.current);
  };

  // 单个组件刷新失败处理
  const apiFail = (refreshKey: string) => {
    apiReqCallback(refreshKey, apiReqSuccessedListRef.current, [...apiReqFailedListRef.current, refreshKey]);
  };

  // 整体刷新成功
  const onRefreshSuccess = () => {
    onSuccess?.();
    notification.success({
      // 未配置则兜底提示默认值
      description: (isEn ? refreshSuccessEnMsg : refreshSuccessCnMsg) || formatMessage({ id: 'component_refresh_success_msg' })
    });
  };

  // 整体刷新失败
  const onRefreshFail = () => {
    onFail?.();
    notification.error({
      // 未配置则兜底提示默认值
      description: (isEn ? refreshFailEnMsg : refreshFailCnMsg) || formatMessage({ id: 'component_refresh_error_msg' })
    });
  };

  // 整体刷新完成
  const onRefreshComplete = () => {
    onComplete?.();
    setForceRefresh(false);
  };

  const dispatchRefresh = (refresh: boolean) => {
    // 重新点击刷新，则置空相关list
    if (refresh) {
      apiReqFailedListRef.current = [];
      apiReqSuccessedListRef.current = [];
    }
    setForceRefresh(refresh);
  };

  // 重置组件相关状态
  const reset = () => {
    apiReqSuccessedListRef.current = [];
    apiReqFailedListRef.current = [];
    apiListRef.current = [];
    setForceRefresh(false);
  };

  return <>
    <RefreshContext.Provider value={
      {
        forceRefresh,
        apiCollect,
        apiSuccess,
        apiFail,
        dispatchRefresh,
        reset
      }}>
      <>{ children }</>
    </RefreshContext.Provider>
  </>;
};

export default Refresh;
